export function normalizeCurrencyCode(value) {
  const currency = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(currency) ? currency : null;
}

export function normalizeFiniteAmount(value) {
  if (
    value == null ||
    typeof value === "boolean" ||
    (typeof value === "string" && value.trim() === "") ||
    (typeof value !== "number" && typeof value !== "string")
  ) {
    return null;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function normalizeSnapshotBoolean(value) {
  return typeof value === "boolean" ? value : null;
}

function hasValue(value) {
  return !(
    value == null ||
    (typeof value === "string" && value.trim() === "")
  );
}

function globalsMatchEntries(section, entries) {
  const hasGlobalCurrency = hasValue(section?.price_currency);
  const hasGlobalAverage = hasValue(section?.avg_recommended_price);

  if (!hasGlobalCurrency && !hasGlobalAverage) return true;
  if (hasGlobalCurrency !== hasGlobalAverage || entries.length !== 1) return false;

  const globalCurrency = normalizeCurrencyCode(section.price_currency);
  const globalAverage = normalizeFiniteAmount(section.avg_recommended_price);
  const entry = entries[0];

  return (
    globalCurrency === entry.currency &&
    globalAverage != null &&
    Math.abs(globalAverage - entry.avg_recommended_price) <= 1e-9
  );
}

export function resolveCurrencyPriceSummary(section) {
  const hasRowsCount = hasValue(section?.rows_count);
  const parsedRowsCount = normalizeFiniteAmount(section?.rows_count);
  const rowsCount =
    Number.isInteger(parsedRowsCount) && parsedRowsCount >= 0
      ? parsedRowsCount
      : null;
  if (hasRowsCount && rowsCount == null) {
    return { status: "unknown", entries: [] };
  }

  const rawBreakdown = section?.price_summary_by_currency;
  const hasBreakdown = Object.prototype.hasOwnProperty.call(
    section || {},
    "price_summary_by_currency"
  );

  if (hasBreakdown && !Array.isArray(rawBreakdown)) {
    return { status: "unknown", entries: [] };
  }

  if (Array.isArray(rawBreakdown)) {
    if (rawBreakdown.length === 0) {
      if (!globalsMatchEntries(section, [])) {
        return { status: "unknown", entries: [] };
      }
      return {
        status: rowsCount === 0 ? "empty" : "unknown",
        entries: [],
      };
    }

    const currencies = new Set();
    const entries = [];
    let coveredRowsCount = 0;

    for (const rawEntry of rawBreakdown) {
      const currency = normalizeCurrencyCode(rawEntry?.currency);
      const average = normalizeFiniteAmount(rawEntry?.avg_recommended_price);
      const entryRowsCount = normalizeFiniteAmount(rawEntry?.rows_count);

      if (
        !currency ||
        average == null ||
        !Number.isInteger(entryRowsCount) ||
        entryRowsCount <= 0 ||
        currencies.has(currency)
      ) {
        return { status: "unknown", entries: [] };
      }

      currencies.add(currency);
      coveredRowsCount += entryRowsCount;
      entries.push({
        currency,
        avg_recommended_price: average,
        rows_count: entryRowsCount,
      });
    }

    if (
      (rowsCount != null && coveredRowsCount !== rowsCount) ||
      !globalsMatchEntries(section, entries)
    ) {
      return { status: "unknown", entries: [] };
    }

    entries.sort((left, right) => left.currency.localeCompare(right.currency));
    return {
      status: entries.length === 1 ? "single" : "multiple",
      entries,
    };
  }

  const legacyCurrency = normalizeCurrencyCode(section?.price_currency);
  const legacyAverage = normalizeFiniteAmount(section?.avg_recommended_price);
  const hasLegacyCurrency = hasValue(section?.price_currency);
  const hasLegacyAverage = hasValue(section?.avg_recommended_price);
  if (
    hasLegacyCurrency &&
    hasLegacyAverage &&
    legacyCurrency &&
    legacyAverage != null &&
    rowsCount !== 0
  ) {
    return {
      status: "single",
      entries: [
        {
          currency: legacyCurrency,
          avg_recommended_price: legacyAverage,
          rows_count: rowsCount != null && rowsCount > 0 ? rowsCount : null,
        },
      ],
    };
  }

  if (hasLegacyCurrency || hasLegacyAverage) {
    return { status: "unknown", entries: [] };
  }

  return {
    status: rowsCount === 0 ? "empty" : "unknown",
    entries: [],
  };
}
